import {
  Corte,
  Fecha,
  FechaDeGrupo,
  InputCorte,
  InputValidez,
} from "src/utils/interfaces";

export const DIAS_DE_LA_SEMANA = [
  "Domingo",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
];
export const MESES_DEL_AÑO = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];
export const UN_DIA_EN_MILIS = 24 * 60 * 60 * 1000;
export const MAX_HORAS_DE_TRABAJO = 8;
export const MAX_HORAS_DE_TRABAJO_MILIS = MAX_HORAS_DE_TRABAJO * 60 * 60 * 1000;
export const MAX_NUMERO_DE_CORTES = 3;

// crea un Date para la fecha en media noche mas la hora deseada en minutos
export function fechaConHora(fecha: Date, tiempoEnMinutos: number): Date {
  const fechaEnMilis = fecha.getTime();
  const tiempoEnMilis = tiempoEnMinutos * 60 * 1000;
  const nuevaFechaEnMilis = fechaEnMilis + tiempoEnMilis;

  return new Date(nuevaFechaEnMilis);
}

// retorna un Date correspondiente al dia siguiente de la fecha
export function diaSiguiente(fecha: Date): Date {
  const diaSgteEnMilis = fecha.getTime() + UN_DIA_EN_MILIS;

  return new Date(diaSgteEnMilis);
}

// retorna cadena con las horas del dia de la fecha con el formato "HH:MM"
export function fechaEnFormatoHHMM(fecha: Date): string {
  const horas = fecha.getUTCHours();
  const minutos = fecha.getUTCMinutes();

  return `${min2Digitos(horas)}:${min2Digitos(minutos)}`;
}

// retorna hora en minutos en base a cadena en formato "HH:MM"
export function HHMMaMinutos(hora: string) {
  const partes = hora.split(":");
  const horas = parseInt(partes[0]);
  const mins = parseInt(partes[1]);

  return horas * 60 + mins;
}

// retorna las horas y minutos del dia en minutos solamente
export function horaEnMins(fecha: Date): number {
  const horas = fecha.getUTCHours();
  const minutos = fecha.getUTCMinutes();

  return 60 * horas + minutos;
}

// retorna cadena con el formato "HH:MM" a partir de la hora en minutos
export function minutosAFormatoHhmm(duracion: number): string {
  const horas = duracion / 60 >= 1 ? Math.floor(duracion / 60) : 0;
  const minutos = (duracion - horas * 60) % 60;

  return `${horas}:${min2Digitos(minutos)}`;
}

// consulta si el tiempo entre horaEntrada y horaSalida no supere al maximo legal
// esta funcion necesita que la hora de entrada sea menor a la de salida
export function duracionDentroDeLoLegal(
  horaEntrada: Date,
  horaSalida: Date
): boolean {
  const antesEnMilis = horaEntrada.getTime();
  const despuesEnMilis = horaSalida.getTime();
  const duracion = despuesEnMilis - antesEnMilis;

  if (duracion > MAX_HORAS_DE_TRABAJO_MILIS) return false;
  else return true;
}

// al modificar un corte, checkear si colisiona con los cortes adyacentes
export function checkearValidezDeCorte(
  actual: InputCorte,
  previo?: InputCorte,
  siguiente?: InputCorte
): InputCorte {
  const nuevo = { ...actual, valido: true, info: "" };

  if (siguiente) {
    const salidaActual = actual.valor?.horaSalida;
    const entradaSiguiente = siguiente.valor?.horaEntrada;

    let salidaEnMins, entradaEnMins;
    if (salidaActual) salidaEnMins = horaEnMins(salidaActual);
    if (entradaSiguiente) entradaEnMins = horaEnMins(entradaSiguiente);

    if (salidaEnMins && entradaEnMins && salidaEnMins >= entradaEnMins) {
      nuevo.valido = false;
      nuevo.info = "Este corte coincide con el corte siguiente";
    }
  }

  if (previo) {
    const salidaPrevia = previo.valor?.horaSalida;
    const entradaActual = actual.valor?.horaEntrada;

    let salidaEnMins, entradaEnMins;
    if (salidaPrevia) salidaEnMins = horaEnMins(salidaPrevia);
    if (entradaActual) entradaEnMins = horaEnMins(entradaActual);

    if (salidaEnMins && entradaEnMins && salidaEnMins >= entradaEnMins) {
      nuevo.valido = false;
      nuevo.info = "Este corte coincide con el corte anterior";
    }
  }

  return nuevo;
}

// checkeos generales de la relacion entrada-salida:
// si estan definidas, si la entrada es menor a la salida
// y si su duracion es menor al maximo general estipulado
export function checkearValidezEntradaSalida(
  entrada?: Date,
  salida?: Date
): InputValidez {
  const validez = {
    valido: true,
    info: "",
  } as InputValidez;

  if (!entrada) {
    validez.valido = false;
    validez.info = "La hora de entrada es obligatoria";
  } else if (!salida) {
    validez.valido = false;
    validez.info = "La hora de salida es obligatoria";
  } else if (horaEnMins(entrada) >= horaEnMins(salida)) {
    validez.valido = false;
    validez.info = "La hora de salida debe ser mayor a la hora de entrada";
  } else if (!duracionDentroDeLoLegal(entrada, salida)) {
    validez.valido = false;
    validez.info = `La duración supera las ${MAX_HORAS_DE_TRABAJO} horas`;
  }

  return validez;
}

// si un numero tiene un solo digito, agrega un cero al frente
export function min2Digitos(numero: number) {
  return numero > 9 ? numero : `0${numero}`;
}

// formatea duracion en minutos al modelo "<horas>hr <minutos>min"
export function formatearIntervalo(duracion: number) {
  if (duracion === 0) return "0min";

  const horas = duracion / 60 >= 1 ? Math.floor(duracion / 60) : 0;
  const minutos = Math.floor((duracion - horas * 60) % 60);

  const espacioIntermedio = horas && minutos ? " " : "";

  return (
    (horas ? `${horas}hr` : "") +
    espacioIntermedio +
    (minutos ? `${minutos}min` : "")
  );
}

// en base a una lista de fechas ordenadas, retorna un grupo con cada fecha distinta en la lista
export function conseguirFechasDeGrupos(registros: Fecha[]): FechaDeGrupo[] {
  const fechasDeGrupos: FechaDeGrupo[] = [];

  let grupoActual: FechaDeGrupo | null = null;
  registros.forEach((registro) => {
    if (
      !grupoActual ||
      grupoActual.fecha.getTime() !== registro.fecha.getTime()
    ) {
      grupoActual = {
        fecha: registro.fecha,
        tiempoTrabajado: registro.tiempoTrabajado,
      };

      fechasDeGrupos.push(grupoActual);
    } else {
      grupoActual.tiempoTrabajado += registro.tiempoTrabajado;
    }
  });

  return fechasDeGrupos;
}

// en base a un fecha, buscar en la lista de grupos de fechas a cual pertenece
export function conseguirGrupoDeFechasActual(
  fechasDeGrupos: FechaDeGrupo[],
  registro: Fecha
) {
  const fechaDeGrupo = fechasDeGrupos.find(
    (grupo) => grupo.fecha.getTime() === registro.fecha.getTime()
  );

  if (fechaDeGrupo) return fechaDeGrupo;
  else return { fecha: new Date(), tiempoTrabajado: 0 };
}

// comparar registros en base a sus campos fecha. usada para ordenar
export function compararHistorialFecha(a: Fecha, b: Fecha) {
  return compararFechas(a.fecha, b.fecha);
}

// compara fechas para saber cual ocurrio antes. usada para ordenar
export function compararFechas(a: Date, b: Date) {
  return a.getTime() - b.getTime();
}

// formatear fecha al modelo local "DD/MM/YYYY"
export function formatearFecha(fecha: Date) {
  const año = fecha.getUTCFullYear();
  const mes = fecha.getUTCMonth() + 1;
  const dia = fecha.getUTCDate();

  return `${min2Digitos(dia)}/${min2Digitos(mes)}/${año}`;
}

export function dateAFormatoGlobal(fecha: Date) {
  const año = fecha.getUTCFullYear();
  const mes = min2Digitos(fecha.getUTCMonth() + 1);
  const dia = min2Digitos(fecha.getUTCDate());

  return `${año}-${mes}-${dia}`;
}

export function fechaComoTexto(fecha: Date) {
  const año = fecha.getUTCFullYear();
  const mes = fecha.getUTCMonth();
  const dia = fecha.getUTCDate();
  const semana = fecha.getUTCDay();

  return `${DIAS_DE_LA_SEMANA[semana]}, ${dia} de ${MESES_DEL_AÑO[mes]}`;
}

export function tiempoTrabajadoEnCortes(cortes: Corte[]) {
  let tiempoTrabajado = 0;

  cortes.forEach((corte) => {
    tiempoTrabajado += tiempoTrabajadoEnCorte(corte);
  });

  return tiempoTrabajado;
}

export function tiempoTrabajadoEnCorte(corte: Corte) {
  const entradaEnMilis = corte.horaEntrada?.getTime() as number;
  const salidaEnMilis = corte.horaSalida?.getTime() as number;

  const tiempoTrabajadoEnMilis = salidaEnMilis - entradaEnMilis;

  return milisAMinutos(tiempoTrabajadoEnMilis);
}

export function milisAMinutos(milis: number) {
  return Math.floor(milis / 60000);
}

/** 'YYYY-MM-DD HH:MM[:SS]' (formato de los backends con SQL directo) -> Date, o null. */
export function fechaDeTextoSql(valor?: string | null): Date | null {
  if (!valor) return null;
  const d = new Date(valor.replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d;
}

/** 'YYYY-MM-DD HH:MM[:SS]' -> 'mié 09/10 · 14:30' (listados). */
export function formatFechaCorta(valor?: string | null): string {
  const d = fechaDeTextoSql(valor);
  if (!d) return valor || "Sin fecha";
  const dia = d.toLocaleDateString("es-PY", { weekday: "short", day: "2-digit", month: "2-digit" });
  const hora = d.toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit" });
  return `${dia} · ${hora}`;
}
